import { create } from "zustand"
import api from "../services/api"
import {
  mesclarTicket,
  mesclarContatoEmTicket,
  ticketsRemoverFilaPorId,
  removeId,
  sortIdsByOldest,
  resolverIdsColunaKanban,
  calcularCountColunaKanban,
  ticketPassaNosFiltros,
} from "../helpers/ticketsStoreHelpers"
import { trelloDragLock } from "../helpers/trelloDragLock"

const removerTicketDeTodasColunasKanban = (oIdsPorSituacao, nTicketId) => {
  const oIdsAtualizado = { ...oIdsPorSituacao }
  Object.keys(oIdsAtualizado).forEach((cColunaId) => {
    oIdsAtualizado[cColunaId] = removeId(oIdsAtualizado[cColunaId] || [], nTicketId)
  })
  return oIdsAtualizado
}

const ajustarCountPaginacaoColunaAposMover = (oPagColuna, nIdsDepois, lColunaOrigem) => {
  const oPag = oPagColuna || { page: 1, hasMore: false }
  if (!oPag.hasMore) {
    return { ...oPag, count: nIdsDepois }
  }
  if (lColunaOrigem) {
    const nBase = oPag.count != null ? oPag.count : nIdsDepois + 1
    return { ...oPag, count: Math.max(0, nBase - 1) }
  }
  const nBase = oPag.count != null ? oPag.count : nIdsDepois - 1
  return { ...oPag, count: nBase + 1 }
}

/** Infere paginação quando o total do servidor excede os tickets retornados na página. */
const resolverHasMoreColunaKanban = (oData, nTicketsRetornados) =>
  Boolean(oData?.hasMore) || Number(oData?.count ?? 0) > Number(nTicketsRetornados ?? 0)

/** Contexto de filtros exclusivo da tela Kanban (não misturar com lista de conversas). */
const oContextoInicialFiltrosKanban = {
  oFilasSelecionadas: [],
  oSituacoesSelecionadas: [],
  oUsuariosSelecionados: [],
  oContatosSelecionados: [],
  oMotivosSelecionados: [],
  canalSelecionado: null,
  cSearchParam: "",
  oFilasDoUserLogado: [],
  isUserPermitted: () => false,
  user: null,
  lApenasNaoLidas: false,
  lFiltrosAtivos: false,
}

const ticketPassaNosFiltrosKanban = (oTicket, oContexto) => {
  if (!oTicket || !oContexto?.user) return true

  if (oContexto.lApenasNaoLidas && !(oTicket.unreadMessages > 0)) {
    return false
  }

  return ticketPassaNosFiltros({
    ticket: oTicket,
    filtros: {
      oFilasSelecionadas: oContexto.oFilasSelecionadas,
      oSituacoesSelecionadas: oContexto.oSituacoesSelecionadas,
      oUsuariosSelecionados: oContexto.oUsuariosSelecionados,
      oContatosSelecionados: oContexto.oContatosSelecionados,
      oMotivosSelecionados: oContexto.oMotivosSelecionados,
      canalSelecionado: oContexto.canalSelecionado,
      cSearchParam: oContexto.cSearchParam,
    },
    user: oContexto.user,
    oFilasDoUserLogado: oContexto.oFilasDoUserLogado,
    isUserPermitted: oContexto.isUserPermitted,
  })
}

const oEstadoInicial = {
  ticketsById: {},
  kanbanIdsBySituacao: {},
  pagination: {
    kanban: {},
  },
  loading: {
    kanban: false,
  },
  lastFetchedAt: {
    kanban: null,
  },
  filterContextKanban: { ...oContextoInicialFiltrosKanban },
}

export const useKanbanTicketsStore = create((set, get) => ({
  ...oEstadoInicial,

  resetarKanbanStore: () => {
    set({ ...oEstadoInicial, filterContextKanban: { ...oContextoInicialFiltrosKanban } })
  },

  definirContextoFiltrosKanban: (oParcial) => {
    set((oState) => ({
      filterContextKanban: { ...oState.filterContextKanban, ...oParcial },
    }))
  },

  /** Limpa colunas antes de recarregar com novos filtros. */
  limparColunasKanban: () => {
    set((state) => ({
      kanbanIdsBySituacao: {},
      pagination: { ...state.pagination, kanban: {} },
    }))
  },

  /** Após exclusão de fila: chip do card passa a "Sem fila". */
  removerFilaDosTickets: (nFilaId) => {
    if (nFilaId == null) return
    set((state) => {
      const ticketsById = ticketsRemoverFilaPorId(state.ticketsById, nFilaId)
      if (ticketsById === state.ticketsById) return {}
      return { ticketsById }
    })
  },

  /**
   * Mantém colunas e tickets do Kanban alinhados ao ticket já mesclado na store de lista (socket / lista).
   */
  mesclarTicketDaListaSocket: (oTicketLista, nUserId) => {
    if (!oTicketLista?.id) return
    set((state) => {
      const oTicketAnterior = state.ticketsById[oTicketLista.id]
      const oTicketMesclado = mesclarTicket(oTicketAnterior, oTicketLista, nUserId)
      const ticketsById = { ...state.ticketsById, [oTicketLista.id]: oTicketMesclado }

      let kanbanIdsBySituacao = { ...state.kanbanIdsBySituacao }
      const cColunaAnterior =
        oTicketAnterior?.situacaoId != null ? String(oTicketAnterior.situacaoId) : null
      const cColunaNova = oTicketMesclado.situacaoId != null ? String(oTicketMesclado.situacaoId) : null

      Object.keys(kanbanIdsBySituacao).forEach((situacaoId) => {
        kanbanIdsBySituacao[situacaoId] = removeId(kanbanIdsBySituacao[situacaoId] || [], oTicketMesclado.id)
      })

      const lPassaFiltros = ticketPassaNosFiltrosKanban(oTicketMesclado, state.filterContextKanban)

      if (lPassaFiltros && cColunaNova != null) {
        // Nova mensagem / mudança de coluna: recoloca e ordena do mais antigo para o mais novo.
        kanbanIdsBySituacao[cColunaNova] = sortIdsByOldest(
          [...(kanbanIdsBySituacao[cColunaNova] || []), oTicketMesclado.id],
          ticketsById,
        )
      }

      const cSituacaoAnterior = cColunaAnterior
      const cNovaSituacao = cColunaNova

      const paginationKanban = { ...state.pagination.kanban }
      if (!lPassaFiltros && cSituacaoAnterior && paginationKanban[cSituacaoAnterior]?.count != null) {
        paginationKanban[cSituacaoAnterior] = {
          ...paginationKanban[cSituacaoAnterior],
          count: Math.max(0, paginationKanban[cSituacaoAnterior].count - 1),
        }
      } else if (lPassaFiltros && cSituacaoAnterior !== cNovaSituacao) {
        if (cSituacaoAnterior && paginationKanban[cSituacaoAnterior]?.count != null) {
          paginationKanban[cSituacaoAnterior] = {
            ...paginationKanban[cSituacaoAnterior],
            count: Math.max(0, paginationKanban[cSituacaoAnterior].count - 1),
          }
        }
        if (cNovaSituacao && paginationKanban[cNovaSituacao]?.count != null) {
          const jaNaColuna = (state.kanbanIdsBySituacao[cNovaSituacao] || []).includes(oTicketMesclado.id)
          if (!jaNaColuna) {
            paginationKanban[cNovaSituacao] = {
              ...paginationKanban[cNovaSituacao],
              count: paginationKanban[cNovaSituacao].count + 1,
            }
          }
        }
      } else if (lPassaFiltros && cNovaSituacao && paginationKanban[cNovaSituacao]?.count != null && !oTicketAnterior) {
        paginationKanban[cNovaSituacao] = {
          ...paginationKanban[cNovaSituacao],
          count: paginationKanban[cNovaSituacao].count + 1,
        }
      }

      return {
        ticketsById,
        kanbanIdsBySituacao,
        pagination: { ...state.pagination, kanban: paginationKanban },
      }
    })
  },

  removerTicketDaListaSocket: (nTicketId, oTicketRemovidoLista) => {
    if (!nTicketId) return
    set((state) => {
      const ticketsById = { ...state.ticketsById }
      if (ticketsById[nTicketId]) {
        delete ticketsById[nTicketId]
      }

      const kanbanIdsBySituacao = Object.keys(state.kanbanIdsBySituacao).reduce((acc, key) => {
        acc[key] = removeId(state.kanbanIdsBySituacao[key] || [], nTicketId)
        return acc
      }, {})

      const paginationKanban = { ...state.pagination.kanban }
      const oTicketRemovido = oTicketRemovidoLista || {}
      if (oTicketRemovido?.situacaoId != null) {
        const key = String(oTicketRemovido.situacaoId)
        if (paginationKanban[key]?.count != null) {
          paginationKanban[key] = {
            ...paginationKanban[key],
            count: Math.max(0, paginationKanban[key].count - 1),
          }
        }
      }

      return {
        ticketsById,
        kanbanIdsBySituacao,
        pagination: { ...state.pagination, kanban: paginationKanban },
      }
    })
  },

  /**
   * Move ticket entre colunas e reordena a coluna de destino do mais antigo para o mais novo.
   */
  moverTicketEntreColunasKanban: (nTicketId, cFromLaneId, cToLaneId, _nIndex, nUserId, oCamposTicket = {}) => {
    if (!nTicketId) return
    set((state) => {
      const oTicketAnterior = state.ticketsById[nTicketId]
      if (!oTicketAnterior) return state

      const cFromKey = String(cFromLaneId)
      const cToKey = String(cToLaneId)
      const oTicketMesclado = mesclarTicket(
        oTicketAnterior,
        { ...oCamposTicket, situacaoId: Number(cToLaneId) },
        nUserId,
      )
      const ticketsById = { ...state.ticketsById, [nTicketId]: oTicketMesclado }

      let kanbanIdsBySituacao = removerTicketDeTodasColunasKanban(state.kanbanIdsBySituacao, nTicketId)
      kanbanIdsBySituacao[cToKey] = sortIdsByOldest(
        [...(kanbanIdsBySituacao[cToKey] || []), nTicketId],
        ticketsById,
      )

      if (cFromKey === cToKey) {
        return { ticketsById, kanbanIdsBySituacao, pagination: state.pagination }
      }

      const paginationKanban = { ...state.pagination.kanban }
      const nIdsFromDepois = (kanbanIdsBySituacao[cFromKey] || []).length
      const nIdsToDepois = (kanbanIdsBySituacao[cToKey] || []).length
      paginationKanban[cFromKey] = ajustarCountPaginacaoColunaAposMover(
        paginationKanban[cFromKey],
        nIdsFromDepois,
        true,
      )
      paginationKanban[cToKey] = ajustarCountPaginacaoColunaAposMover(
        paginationKanban[cToKey],
        nIdsToDepois,
        false,
      )

      return {
        ticketsById,
        kanbanIdsBySituacao,
        pagination: { ...state.pagination, kanban: paginationKanban },
      }
    })
  },

  inserirOuAtualizarTicket: (oTicket, nUserId) => {
    if (!oTicket?.id) return
    set((state) => {
      const oTicketAnterior = state.ticketsById[oTicket.id]
      const oTicketMesclado = mesclarTicket(oTicketAnterior, oTicket, nUserId)
      const ticketsById = { ...state.ticketsById, [oTicket.id]: oTicketMesclado }

      let kanbanIdsBySituacao = { ...state.kanbanIdsBySituacao }
      const cColunaAnterior =
        oTicketAnterior?.situacaoId != null ? String(oTicketAnterior.situacaoId) : null
      const cColunaNova = oTicketMesclado.situacaoId != null ? String(oTicketMesclado.situacaoId) : null

      Object.keys(kanbanIdsBySituacao).forEach((situacaoId) => {
        kanbanIdsBySituacao[situacaoId] = removeId(kanbanIdsBySituacao[situacaoId] || [], oTicketMesclado.id)
      })

      if (cColunaNova != null) {
        kanbanIdsBySituacao[cColunaNova] = sortIdsByOldest(
          [...(kanbanIdsBySituacao[cColunaNova] || []), oTicketMesclado.id],
          ticketsById,
        )
      }

      const cSituacaoAnterior = cColunaAnterior
      const cNovaSituacao = cColunaNova

      const paginationKanban = { ...state.pagination.kanban }
      if (cSituacaoAnterior !== cNovaSituacao) {
        if (cSituacaoAnterior && paginationKanban[cSituacaoAnterior]?.count != null) {
          paginationKanban[cSituacaoAnterior] = {
            ...paginationKanban[cSituacaoAnterior],
            count: Math.max(0, paginationKanban[cSituacaoAnterior].count - 1),
          }
        }
        if (cNovaSituacao && paginationKanban[cNovaSituacao]?.count != null) {
          const jaNaColuna = (state.kanbanIdsBySituacao[cNovaSituacao] || []).includes(oTicketMesclado.id)
          if (!jaNaColuna) {
            paginationKanban[cNovaSituacao] = {
              ...paginationKanban[cNovaSituacao],
              count: paginationKanban[cNovaSituacao].count + 1,
            }
          }
        }
      } else if (cNovaSituacao && paginationKanban[cNovaSituacao]?.count != null && !oTicketAnterior) {
        paginationKanban[cNovaSituacao] = {
          ...paginationKanban[cNovaSituacao],
          count: paginationKanban[cNovaSituacao].count + 1,
        }
      }

      return {
        ticketsById,
        kanbanIdsBySituacao,
        pagination: { ...state.pagination, kanban: paginationKanban },
      }
    })
  },

  /**
   * Reflete a edição de um contato nos cards do Kanban em tempo real, sem refetch.
   * Atualiza ticket.contact de todos os tickets que referenciam o contato editado.
   */
  atualizarContatoNosTickets: (oContato) => {
    if (!oContato?.id) return
    set((state) => {
      let lAlterou = false
      const ticketsById = { ...state.ticketsById }
      Object.keys(ticketsById).forEach((cId) => {
        const oTicket = ticketsById[cId]
        const oTicketAtualizado = mesclarContatoEmTicket(oTicket, oContato)
        if (oTicketAtualizado !== oTicket) {
          ticketsById[cId] = oTicketAtualizado
          lAlterou = true
        }
      })
      if (!lAlterou) return state
      return { ticketsById }
    })
  },

  removerTicket: (nTicketId) => {
    if (!nTicketId) return
    set((state) => {
      const ticketsById = { ...state.ticketsById }
      const oTicketRemovido = ticketsById[nTicketId]
      delete ticketsById[nTicketId]

      const kanbanIdsBySituacao = Object.keys(state.kanbanIdsBySituacao).reduce((acc, key) => {
        acc[key] = removeId(state.kanbanIdsBySituacao[key] || [], nTicketId)
        return acc
      }, {})

      const paginationKanban = { ...state.pagination.kanban }
      if (oTicketRemovido?.situacaoId != null) {
        const key = String(oTicketRemovido.situacaoId)
        if (paginationKanban[key]?.count != null) {
          paginationKanban[key] = {
            ...paginationKanban[key],
            count: Math.max(0, paginationKanban[key].count - 1),
          }
        }
      }

      return {
        ticketsById,
        kanbanIdsBySituacao,
        pagination: { ...state.pagination, kanban: paginationKanban },
      }
    })
  },

  aplicarColunaKanbanNoEstado: (situacaoId, data, page, oOpcoes = {}) => {
    if (trelloDragLock.isLocked()) {
      trelloDragLock.adiarAplicacaoKanban({ situacaoId, data, page, oOpcoes })
      return
    }
    get().aplicarColunaKanbanNoEstadoDireto(situacaoId, data, page, oOpcoes)
  },

  aplicarColunaKanbanNoEstadoDireto: (situacaoId, data, page, oOpcoes = {}) => {
    const lSubstituirColuna = Boolean(oOpcoes.substituirColuna)
    const tickets = Array.isArray(data?.tickets) ? data.tickets : []
    set((state) => {
      let ticketsById = { ...state.ticketsById }
      const key = String(situacaoId)
      const idsBase = [...(state.kanbanIdsBySituacao[key] || [])]

      tickets.forEach((t) => {
        const merged = mesclarTicket(ticketsById[t.id], t, undefined)
        ticketsById[t.id] = merged
      })

      const columnIds = resolverIdsColunaKanban(
        idsBase,
        tickets,
        page,
        ticketsById,
        key,
        lSubstituirColuna,
      )
      const lHasMoreColuna = resolverHasMoreColunaKanban(data, columnIds.length)
      const nCount = calcularCountColunaKanban({
        nCountAnterior: lSubstituirColuna ? null : state.pagination.kanban[key]?.count,
        nIdsAntes: lSubstituirColuna ? 0 : idsBase.length,
        nIdsDepois: columnIds.length,
        nCountServidor: data?.count,
        page,
        lHasMore: lHasMoreColuna,
      })

      return {
        ticketsById,
        kanbanIdsBySituacao: {
          ...state.kanbanIdsBySituacao,
          [key]: columnIds,
        },
        pagination: {
          ...state.pagination,
          kanban: {
            ...state.pagination.kanban,
            [key]: { page, hasMore: lHasMoreColuna, count: nCount },
          },
        },
      }
    })
  },

  buscarTicketsKanban: async (situacaoId, params = {}, page = 1, oOpcoes = {}) => {
    if (!situacaoId) return
    const lSilencioso = Boolean(oOpcoes.silent)
    if (!lSilencioso) {
      set((state) => ({ loading: { ...state.loading, kanban: true } }))
    }
    try {
      const { data } = await api.get("/ticket/kanban", {
        params: {
          ...params,
          situacoes: JSON.stringify([Number(situacaoId)]),
          pagina: page,
        },
      })

      useKanbanTicketsStore.getState().aplicarColunaKanbanNoEstado(situacaoId, data, page, oOpcoes)
      set((state) => ({
        ...(lSilencioso ? {} : { loading: { ...state.loading, kanban: false } }),
        lastFetchedAt: { ...state.lastFetchedAt, kanban: Date.now() },
      }))
    } catch (oErro) {
      if (!lSilencioso) {
        set((state) => ({ loading: { ...state.loading, kanban: false } }))
      }
      throw oErro
    }
  },

  /**
   * Uma requisição HTTP para várias colunas (back executa em paralelo).
   */
  buscarTicketsKanbanLote: async (mSituacaoIds, params = {}, page = 1, oOpcoes = {}) => {
    if (!mSituacaoIds?.length) return
    const lSilencioso = Boolean(oOpcoes.silent)
    const lSubstituirColuna = Boolean(oOpcoes.substituirColuna)
    if (!lSilencioso) {
      set((state) => ({ loading: { ...state.loading, kanban: true } }))
    }
    try {
      const { data } = await api.get("/ticket/kanban/lote", {
        params: {
          ...params,
          situacoes: JSON.stringify(mSituacaoIds.map(Number)),
          pagina: page,
        },
      })

      const colunas = data?.colunas || {}
      if (trelloDragLock.isLocked()) {
        Object.keys(colunas).forEach((key) => {
          trelloDragLock.adiarAplicacaoKanban({
            situacaoId: key,
            data: colunas[key],
            page,
            oOpcoes: { substituirColuna: lSubstituirColuna },
          })
        })
        set((state) => ({
          ...(lSilencioso ? {} : { loading: { ...state.loading, kanban: false } }),
          lastFetchedAt: { ...state.lastFetchedAt, kanban: Date.now() },
        }))
        return
      }

      set((state) => {
        let ticketsById = { ...state.ticketsById }
        let kanbanIdsBySituacao = { ...state.kanbanIdsBySituacao }
        let paginationKanban = { ...state.pagination.kanban }

        Object.keys(colunas).forEach((key) => {
          const coluna = colunas[key]
          const tickets = Array.isArray(coluna?.tickets) ? coluna.tickets : []
          const idsBase = [...(kanbanIdsBySituacao[key] || [])]

          tickets.forEach((t) => {
            ticketsById[t.id] = mesclarTicket(ticketsById[t.id], t, undefined)
          })

          const columnIds = resolverIdsColunaKanban(
            idsBase,
            tickets,
            page,
            ticketsById,
            key,
            lSubstituirColuna,
          )
          kanbanIdsBySituacao[key] = columnIds
          const lHasMoreColuna = resolverHasMoreColunaKanban(coluna, columnIds.length)
          const nCount = calcularCountColunaKanban({
            nCountAnterior: lSubstituirColuna ? null : state.pagination.kanban[key]?.count,
            nIdsAntes: lSubstituirColuna ? 0 : idsBase.length,
            nIdsDepois: columnIds.length,
            nCountServidor: coluna?.count,
            page,
            lHasMore: lHasMoreColuna,
          })
          paginationKanban[key] = {
            page,
            hasMore: lHasMoreColuna,
            count: nCount,
          }
        })

        return {
          ticketsById,
          kanbanIdsBySituacao,
          pagination: { ...state.pagination, kanban: paginationKanban },
          ...(lSilencioso ? {} : { loading: { ...state.loading, kanban: false } }),
          lastFetchedAt: { ...state.lastFetchedAt, kanban: Date.now() },
        }
      })
    } catch (oErro) {
      if (!lSilencioso) {
        set((state) => ({ loading: { ...state.loading, kanban: false } }))
      }
      throw oErro
    }
  },
}))

export const selecionarColunaKanban = (situacaoId) => (state) =>
  (state.kanbanIdsBySituacao[String(situacaoId)] || []).map((id) => state.ticketsById[id]).filter(Boolean)

trelloDragLock.registrarAplicadorKanban((mPendentes) => {
  mPendentes.forEach(({ situacaoId, data, page, oOpcoes }) => {
    useKanbanTicketsStore.getState().aplicarColunaKanbanNoEstadoDireto(situacaoId, data, page, oOpcoes)
  })
})
